import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
  ElementRef,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NOpsNumberDirective } from '../ops-directives/n-ops-number.directive';

/**
 * inputs:
 * -------
 * input label?: string
 * input placeholder?: string
 * input mandatory?: boolean
 * input formName?: string
 * input type text|email|phone|credit-card|cvc|password|custom
 * input customValidation? regexp|method
 * input noteText?: string
 * input isDisabled?: boolean
 * input setFocus?: boolean
 * input errorMessage?: string
 * input showError?: boolean
 * ---------------------------
 * events:
 * -------
 * output focused
 * output blured
 * output clicked
 * output changed
 * output validationError
 */

@Component({
  selector: 'ops-input',
  standalone: true,
  imports: [CommonModule, FormsModule, NOpsNumberDirective, TranslateModule],
  templateUrl: './ops-input.component.html',
  styleUrl: './ops-input.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class OpsInputComponent implements OnInit {
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() mandatory?: boolean = false;
  @Input() formName?: string;
  @Input() isPreview?: boolean = false;
  @Input() beforeIcon?: string = '';
  @Input() passwordView?: boolean = false;
  @Input() type:
    | 'text'
    | 'email'
    | 'phone'
    | 'credit-card'
    | 'cvc'
    | 'password'
    | 'cmonth'
    | 'day'
    | 'year'
    | 'birthYear'
    | 'last4digits'
    | 'url'
    | 'custom' = 'text';
  @Input() customValidation?: RegExp | ((value: string) => boolean);
  @Input() noteText?: string;
  @Input() isDisabled?: boolean = false;
  @Input() setFocus?: boolean = false;
  @Input() errorMessage?: string;
  @Input() showError?: boolean = false;

  @Input() extendClass?: string;

  @Output() focused = new EventEmitter<void>();
  @Output() blured = new EventEmitter<void>();
  @Output() clicked = new EventEmitter<void>();
  @Output() changed = new EventEmitter<string>();
  @Output() validationError = new EventEmitter<string>();

  @Input() decimalValue?: boolean = false;

  private _value: string;

  @Input() set value(val: string) {
    this._value = val;

    if (this.decimalValue) {
      // const numericValue = parseFloat(val.replace(/[^0-9.-]+/g, ''));
      this._value = val.toString(); //isNaN(numericValue) ? '0' : numericValue.toString();
    } else {
      this.validateInput();
    }
  }

  get value(): string {
    return this._value;
  }

  prevElement?: any;

  constructor(
    private el: ElementRef,
    private translateService: TranslateService
  ) {}

  // Predefined validation patterns for specific types
  private validationPatterns = {
    email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    phone: /^\+?[0-9]{10,14}$/,
    'credit-card': /^[0-9]{16}$/,
    cvc: /^[0-9]{3,4}$/,
    last4digits: /^[0-9]{4}$/,
    password: /.{6,}/, // At least 8 characters
    cmonth: /^(0?[1-9]|1[0-2])$/,
    day: /^(0?[1-9]|1[0-9]|2[0-9]|3[0-1])$/,
    year: /^(20[0-9]{2})$/,
    birthYear: /^(19[0-9]{2}|20[0.9]{2})$/,
    url: /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,6}(\/[a-zA-Z0-9#\\/?&=._-]*)?$/,
  };

  private sanitizeValidationPatterns = {
    email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/g,
    phone: /^\+?[0-9]{10,14}$/g,
    'credit-card': /^[0-9]{16}$/g,
    cvc: /^[0-9]{3,4}$/g,
    last4digits: /^[0-9]{4}$/g,
    password: /.{6,}/g, // At least 8 characters
    cmonth: /^(0?[1-9]|1[0-2])$/g,
    day: /^(0?[1-9]|1[0-9]|2[0-9]|3[0-1])$/g,
    year: /^(20[0-9]{2})$/g,
    birthYear: /^(19[0-9]{2}|20[0.9]{2})$/g,
    url: /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,6}(\/[a-zA-Z0-9#\\/?&=._-]*)?$/g,
  };

  // Character limitations based on type
  private maxLength = {
    phone: 14,
    'credit-card': 16,
    cvc: 4,
    last4digits: 4,
    password: 128, // Example max length for passwords (can vary)
    email: 254, // Max email length according to standards
    cmonth: 2,
    day: 2,
    year: 4,
    birthYear: 4,
  };

  resolveInputType(type: string): string {
    if (this.passwordView && this.passwordVisible) {
      return 'text';
    }

    return type === 'custom' ? 'text' : type;
  }

  passwordVisible = false;

  onSwitchPassword() {
    this.passwordVisible = !this.passwordVisible;
    this.fixFocus();
  }

  onFocus(event: any) {
    if (!this.prevElement) {
      this.prevElement = event?.composedPath()[0];
    }

    this.focused.emit();
  }

  onBlur() {
    this.validateInput(true);
    this.changed.emit(this.value);
    this.blured.emit();
  }

  onBlurNumeric(value: string) {
    const numericValue = parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
    this.value = value || '0';
    this.validateInput(true);
    this.changed.emit(numericValue.toString());
    this.blured.emit();
  }

  onInputChange(event: Event) {
    const inputValue = (event.target as HTMLInputElement).value;
    if (this.isValidInput(inputValue)) {
      this.value = inputValue;
      this.changed.emit(inputValue);
    }
  }

  get showNoteText(): boolean {
    if ((this.showError && this.errorMessage) || this.isPreview) return false;

    return !!this.noteText;
  }

  validateInput(showError?: boolean) {
    // Custom validation logic if provided
    if (this.customValidation) {
      if (
        typeof this.customValidation === 'function' &&
        !this.customValidation(this.value)
      ) {
        this.showError = showError;
        this.errorMessage =
          this.errorMessage ||
          this.translateService.instant('validation.invalidInput');
        this.validationError.emit(this.errorMessage);
        return;
      } else if (
        this.customValidation instanceof RegExp &&
        !this.customValidation.test(this.value)
      ) {
        this.showError = showError;
        this.errorMessage =
          this.errorMessage ||
          this.translateService.instant('validation.invalidInput');
        this.validationError.emit(this.errorMessage);
        return;
      }

      this.validationError.emit('');
      this.showError = false; // If everything is valid
      return;
    }

    if (this.mandatory && !this.value) {
      this.showError = showError;
      this.errorMessage = this.translateService.instant('validation.required');
      this.validationError.emit(this.errorMessage);
      return;
    }

    if (!this.mandatory && !this.value) {
      this.showError = false;
      this.validationError.emit('');
      return;
    }

    // If the input type has a predefined validation pattern
    if (this.type !== 'custom' && this.validationPatterns[this.type]) {
      const pattern = this.validationPatterns[this.type];
      if (!pattern.test(this.value)) {
        if (showError) this.showError = showError;
        this.errorMessage = this.getErrorMessageByType();
        this.validationError.emit(this.errorMessage);
        return;
      }
    }

    this.validationError.emit('');
    this.showError = false; // If everything is valid
  }

  getMaxAllowedByType() {
    switch (this.type) {
      case 'cvc':
        return 4;
      case 'credit-card':
        return 16;
      case 'phone':
        return 14;
      case 'cmonth':
        return 2;
      case 'day':
        return 2;
      case 'year':
        return 4;
      case 'birthYear':
        return 4;
      case 'last4digits':
        return 4;
      default:
        return 250;
    }
  }

  onAmountChange(value: string) {
    // Remove any non-numeric characters
    // const numericValue = parseFloat(value.replace(/[^0-9.-]+/g, ''));
    // this.value = value;
    // this.changed.emit(numericValue.toString());
  }

  onPaste(event: ClipboardEvent) {
    const pastedData = event.clipboardData?.getData('text') || '';
    const sanitizedData = this.sanitizeInput(pastedData);

    // If sanitized data exceeds max length, truncate it
    if (sanitizedData.length > this.getMaxAllowedByType()) {
      event.preventDefault();
      this.value = sanitizedData.substring(0, this.getMaxAllowedByType());
    } else {
      event.preventDefault();

      const pastedText = event.clipboardData?.getData('text') || '';
      const input = this.el.nativeElement.getElementsByTagName('input')[0];

      const pastedTextTrim = pastedText.trim();

      if (this.type === 'email') {
        input.value = pastedTextTrim;
        this.value = pastedTextTrim;

        this.changed.emit(pastedTextTrim);
      } else {
        // Get the current cursor position and selected text range
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;

        // Insert the pasted text at the cursor position
        const textBefore = input.value.substring(0, start);
        const textAfter = input.value.substring(end);

        const textToApply = textBefore + pastedTextTrim + textAfter;

        // Update the input field value
        input.value = textToApply;

        // Set the new cursor position after the pasted text
        const newCursorPosition = start + pastedTextTrim.length;

        input.setSelectionRange(newCursorPosition, newCursorPosition);

        this.value = textToApply;
        this.changed.emit(textToApply);
      }
    }
    // Prevent default paste behavior
    event.preventDefault();
  }

  sanitizeInput(input: string): string {
    // Use a regular expression to keep only numeric characters
    if (input.match(this.sanitizeValidationPatterns[this.type])) {
      return input;
    }
    return '';
  }

  mouseDown = 0;

  fixFocus() {
    setTimeout(() => {
      try {
        const input = this.el.nativeElement.getElementsByTagName('input')[0];
        input.focus();
        if (this.type !== 'email') {
          const valLen = input.value.length;
          input.setSelectionRange(valLen, valLen);
        }
      } catch (e) {
        // Silently handle focus error - input might not be available
      }
    }, 20);
  }

  onClick(event: MouseEvent) {
    // const inputEl = this.el.nativeElement.getElementsByTagName('input');
    if (this.mouseDown < 180 && event['pointerType'] !== 'touch') {
      this.el.nativeElement.getElementsByTagName('input')[0].select();
    }
  }

  onMouseDown(event: MouseEvent) {
    this.mouseDown = new Date().getTime();
  }

  onMouseUp(event: MouseEvent) {
    this.mouseDown = new Date().getTime() - this.mouseDown;
  }

  onKeydown(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'Delete',
      'Tab',
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (event.key === 'Backspace' || event.key === 'Delete') {
      return;
    }

    if (
      this.type === 'cvc' ||
      this.type === 'last4digits' ||
      this.type === 'credit-card' ||
      this.type === 'phone' ||
      this.type === 'cmonth' ||
      this.type === 'day' ||
      this.type === 'year' ||
      this.type === 'birthYear'
    ) {
      const isNumberKey = event.key >= '0' && event.key <= '9';

      if (this.value?.length >= this.getMaxAllowedByType() && isNumberKey) {
        event.preventDefault();
        return;
      }

      // If the key is not a number, prevent input
      if (
        this.type === 'phone' &&
        !isNumberKey &&
        event.key === '+' &&
        this.value?.length === 0
      ) {
        // * just to skip case
      } else if (!isNumberKey) {
        event.preventDefault();
      }
    }
    // Allow special keys like backspace, arrow keys, etc.
  }

  onKeydownAmount(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'Delete',
      'Tab',
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (event.key === 'Backspace' || event.key === 'Delete') {
      return;
    }

    const isNumberKey = event.key >= '0' && event.key <= '9';

    if (this.value?.length >= this.getMaxAllowedByType() && isNumberKey) {
      event.preventDefault();
      return;
    }

    if (!isNumberKey) {
      event.preventDefault();
    }
  }

  // Prevent invalid characters based on type
  isValidInput(inputValue: string): boolean {
    switch (this.type) {
      case 'phone':
        return /^\+?[0-9]*$/.test(inputValue); // Only numbers and optional '+' allowed
      case 'credit-card':
        return /^[0-9]*$/.test(inputValue); // Only numbers allowed
      case 'cvc':
        return /^[0-9]*$/.test(inputValue); // Only numbers allowed
      case 'last4digits':
        return /^[0-9]*$/.test(inputValue); // Only numbers allowed
      case 'email':
        return /^[a-zA-Z0-9._%+-@]*$/.test(inputValue); // Email allowed characters
      case 'day':
        return /^[a-zA-Z0-9._%+-@]*$/.test(inputValue); // Email allowed characters
      case 'cmonth':
        return /^[a-zA-Z0-9._%+-@]*$/.test(inputValue); // Email allowed characters
      case 'year':
        return /^[a-zA-Z0-9._%+-@]*$/.test(inputValue); // Email allowed characters
      case 'url':
        return /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,6}(\/[a-zA-Z0-9#\\/?&=._-]*)?$/.test(
          inputValue
        );
      case 'password':
        // Passwords can have almost any character but enforce a max length
        return true;
      default:
        return true; // For text and custom types, allow any characters
    }
  }

  // Set max length based on input type
  getMaxLength(): number | undefined {
    return this.maxLength[this.type] || undefined;
  }

  // Helper method to get error message based on the input type
  getErrorMessageByType(): string {
    switch (this.type) {
      case 'email':
        return this.translateService.instant('validation.email');
      case 'phone':
        return this.translateService.instant('validation.phone');
      case 'credit-card':
        return this.translateService.instant('validation.creditCard');
      case 'cvc':
        return this.translateService.instant('validation.cvc');
      case 'last4digits':
        return this.translateService.instant('validation.last4digits');
      case 'day':
        return this.translateService.instant('validation.day');
      case 'cmonth':
        return this.translateService.instant('validation.cmonth');
      case 'year':
        return this.translateService.instant('validation.year');
      case 'birthYear':
        return this.translateService.instant('validation.birthYear');
      case 'password':
        return this.translateService.instant('validation.password');
      case 'url':
        return this.translateService.instant('validation.url');
      default:
        return this.translateService.instant('validation.invalidInput');
    }
  }

  ngOnInit() {
    // Initialize any additional logic if needed
  }
}
